/**
 * Link Check API
 */

import { api, ApiError, type ApiResponse, type ApiListResponse } from '@/lib/api';

export type LinkStatus =
    | 'alive'
    | 'dead'
    | 'timeout'
    | 'server_error'
    | 'dns_error'
    | 'connection_error'
    | 'ssl_error'
    | 'redirect_loop'
    | 'invalid_url'
    | 'error';

export interface LinkCheckResult {
    status: LinkStatus;
    statusCode?: number;
    errorMessage?: string;
}

export interface DeadLink {
    id: string;
    collection: string;
    item_id: string;
    field: string;
    url: string;
    status: LinkStatus;
    status_code: number | null;
    error_message: string | null;
    last_checked: string;
    date_created: string;
    date_updated: string;
}

export interface ScanJob {
    id: string;
    status: 'running' | 'completed' | 'failed';
    collection: string | null;
    started_at: string;
    heartbeat_at: string;
    completed_at: string | null;
    total: number;
    checked: number;
    dead: number;
    error_message: string | null;
    triggered_by: string | null;
}

export interface ScanTriggerResult {
    job: ScanJob;
    alreadyRunning: boolean;
}

export async function checkUrl(url: string): Promise<LinkCheckResult> {
    const response = await api.post<ApiResponse<LinkCheckResult>>('/utils/link-check', { url });
    return response.data;
}

export async function getDeadLinkStatus(
    collection: string,
    itemId: string,
    field: string,
): Promise<DeadLink | null> {
    try {
        const params = new URLSearchParams({ collection, item_id: itemId, field });
        const response = await api.get<ApiResponse<DeadLink>>(
            `/utils/link-check/status?${params.toString()}`,
        );
        return response.data;
    } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
    }
}

export async function triggerScan(collection?: string): Promise<ScanTriggerResult> {
    try {
        const response = await api.post<ApiResponse<ScanJob>>('/utils/link-check/scan', {
            ...(collection ? { collection } : {}),
        });
        return { job: response.data, alreadyRunning: false };
    } catch (err) {
        // The API returns 409 with the existing running job in `data`.
        // ApiError doesn't carry the body, so refetch the latest job.
        if (err instanceof ApiError && err.status === 409) {
            const latest = await getLatestScan();
            if (latest) return { job: latest, alreadyRunning: true };
        }
        throw err;
    }
}

export async function getLatestScan(): Promise<ScanJob | null> {
    const response = await api.get<ApiResponse<ScanJob | null>>('/utils/link-check/scan/latest');
    return response.data ?? null;
}

export async function getUrlCollections(): Promise<string[]> {
    const response = await api.get<ApiResponse<string[]>>('/utils/link-check/url-collections');
    return response.data ?? [];
}

export async function getDeadLinks(params: {
    collection?: string;
    status?: LinkStatus;
    page?: number;
    limit?: number;
} = {}): Promise<{ data: DeadLink[]; total: number }> {
    const query = new URLSearchParams();
    if (params.collection) query.set('collection', params.collection);
    if (params.status) query.set('status', params.status);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await api.get<ApiListResponse<DeadLink>>(`/utils/dead-links${suffix}`);
    return {
        data: response.data ?? [],
        total: response.meta?.total_count ?? 0,
    };
}

export async function clearAllDeadLinks(): Promise<number> {
    const response = await api.delete<ApiResponse<{ deleted: number }>>('/utils/dead-links');
    return response.data.deleted;
}

export async function removeDeadLink(id: string): Promise<void> {
    await api.delete<ApiResponse<{ deleted: number }>>(`/utils/dead-links/${id}`);
}
